/** Quién descansa cada jornada (Eduardo 2026-10-05: «nos falta agregar a los
 * que descansan (BYE) para que no se pierdan»). El servidor ya decide quién
 * descansa (`rounds.jornada_dates.jornada_overview`: por jornada, el mismo
 * criterio de «Descansas esta jornada», pero también con calendario de
 * canchas); aquí solo se arma el texto de los PDF y de las pantallas que
 * listan una jornada. */

import type { JornadaRestEntry, JornadaSavedWindow } from "./api";
import { namesInSeveralDivisions } from "./groupLabels";
import { savedJornadaRange } from "./jornadaDates";
import { escapeHtml } from "./printTemplate";

/** Descansos de la jornada `roundNumber`; con `groupIds`, solo de esos grupos
 * (los que la pantalla o la sección del PDF muestran). */
export function restsForJornada(
  entries: ReadonlyArray<JornadaRestEntry> | null | undefined,
  roundNumber: number | null | undefined,
  groupIds?: ReadonlySet<number> | null,
): JornadaRestEntry[] {
  if (!entries || roundNumber === null || roundNumber === undefined) return [];
  return entries.filter(
    (entry) =>
      entry.round_number === roundNumber &&
      entry.participants.length > 0 &&
      (!groupIds || groupIds.has(entry.group_id)),
  );
}

/** «Alex», «Alex y Dani», «Alex, Beto y Dani». */
function joinNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} y ${names[names.length - 1]}`;
}

/** Nombre del grupo; «Grupo 1 · Sub-12» si el nombre se repite entre las
 * categorías de la lista o está en `repeatedNames` (los repetidos de lo que
 * muestra la pantalla o el PDF: si la tabla dice «Grupo 1 (Sub-10)», el
 * renglón de descansos también lleva la categoría). */
function groupLabels(
  entries: ReadonlyArray<JornadaRestEntry>,
  repeatedNames?: ReadonlySet<string> | null,
): Map<number, string> {
  const repeated = namesInSeveralDivisions(
    entries.map((entry) => ({ name: entry.group_name, divisionKey: entry.division_id })),
  );
  repeatedNames?.forEach((name) => repeated.add(name.trim()));
  const labels = new Map<number, string>();
  for (const entry of entries) {
    const name = entry.group_name.trim();
    const division = (entry.division_name ?? "").trim();
    labels.set(entry.group_id, repeated.has(name) && division ? `${name} · ${division}` : name);
  }
  return labels;
}

/** Renglón del PDF: «Descansan: Juan Pérez (Grupo A.1) · Ana López (Grupo
 * B.1)»; uno solo, «Descansa: …». Null si nadie descansa. */
export function restPrintLine(
  entries: ReadonlyArray<JornadaRestEntry>,
  repeatedNames?: ReadonlySet<string> | null,
): string | null {
  const labels = groupLabels(entries, repeatedNames);
  const items = entries.flatMap((entry) =>
    entry.participants.map((participant) => `${participant.name} (${labels.get(entry.group_id) ?? entry.group_name})`),
  );
  if (items.length === 0) return null;
  return `${items.length === 1 ? "Descansa" : "Descansan"}: ${items.join(" · ")}`;
}

/** El mismo renglón en HTML para debajo de la tabla de la jornada. */
export function restPrintHtml(
  entries: ReadonlyArray<JornadaRestEntry>,
  repeatedNames?: ReadonlySet<string> | null,
): string {
  const line = restPrintLine(entries, repeatedNames);
  return line ? `<p class="print-rest-line" data-print-rests>${escapeHtml(line)}</p>` : "";
}

/** Renglón de pantalla, uno por grupo: «Descansa esta jornada: Alex».
 * `withGroup`: en una lista que junta grupos, «… : Alex (Grupo A.1)».
 * `meId`: el jugador que mira lee «Alex (tú)». */
export function restScreenLine(
  entry: JornadaRestEntry,
  options: { withGroup?: boolean; groupLabel?: string; meId?: number | null } = {},
): string {
  const names = entry.participants.map((participant) =>
    options.meId != null && participant.id === options.meId ? `${participant.name} (tú)` : participant.name,
  );
  const group = options.withGroup ? ` (${options.groupLabel ?? entry.group_name})` : "";
  return `${names.length === 1 ? "Descansa" : "Descansan"} esta jornada: ${joinNames(names)}${group}`;
}

/** Renglones de pantalla de varios grupos (cada uno con su grupo). */
export function restScreenLines(
  entries: ReadonlyArray<JornadaRestEntry>,
  repeatedNames?: ReadonlySet<string> | null,
): Array<{ key: number; text: string }> {
  const labels = groupLabels(entries, repeatedNames);
  return entries.map((entry) => ({
    key: entry.group_id,
    text: restScreenLine(entry, { withGroup: true, groupLabel: labels.get(entry.group_id) }),
  }));
}

/** «Descansa: Alex» / «Descansan: Alex y Dani» (dentro de un grupo). */
export function restNamesLine(entry: JornadaRestEntry): string | null {
  const names = entry.participants.map((participant) => participant.name);
  if (names.length === 0) return null;
  return `${names.length === 1 ? "Descansa" : "Descansan"}: ${joinNames(names)}`;
}

/** Hoja del Round Robin (Draw), debajo de la tabla de cada grupo: cada
 * jornada con sus fechas GUARDADAS y quién descansa, «Jornada 1 · del martes
 * 6 al domingo 11 de octubre de 2026 · Descansa: Alex». Solo las jornadas con
 * fechas o descanso; cadena vacía si ninguna tiene. */
export function groupJornadasPrintHtml(options: {
  roundNumbers: Iterable<number>;
  groupId: number;
  divisionId: number | null;
  windows: ReadonlyArray<JornadaSavedWindow>;
  rests: ReadonlyArray<JornadaRestEntry>;
}): string {
  const numbers = [...new Set(options.roundNumbers)].sort((a, b) => a - b);
  const groupIds = new Set([options.groupId]);
  const items: string[] = [];
  for (const numero of numbers) {
    const range = savedJornadaRange(options.windows, numero, [options.divisionId]);
    const rest = restsForJornada(options.rests, numero, groupIds)[0] ?? null;
    const restLine = rest ? restNamesLine(rest) : null;
    if (!range && !restLine) continue;
    const label =
      options.windows.find(
        (window) => window.round_number === numero && (window.division_id ?? null) === options.divisionId,
      )?.label ??
      rest?.round_label ??
      `Jornada ${numero}`;
    const detail = [range, restLine].filter(Boolean).join(" · ");
    items.push(`<li><strong>${escapeHtml(label)}</strong> · ${escapeHtml(detail)}</li>`);
  }
  if (items.length === 0) return "";
  return `<div class="rr-print-jornadas" data-print-jornadas><ul>${items.join("")}</ul></div>`;
}
